<script setup lang="ts">
import type { TrackingT } from '~/types/tracking'
import { promiseTimeout, useDebounceFn, watchDebounced } from '@vueuse/core'
import type { Expedition } from '~/shared/expedition'

const MAX_AWB_COUNT = 3

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const values = ref<string[]>([])
const input = useTemplateRef('input')

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    courier?: Expedition
  }>(),
  {
    title: 'Lacak Paket Real Time <br/> & Akurat',
    description:
      'Lebih mudah cek resi untuk mengetahui update terbaru pengiriman paketmu dengan KiriminAja.',
    courier: undefined
  }
)

const result = ref<Record<string, Partial<TrackingT>>>()
const sanitizeValue = computed(() => {
  return values.value.map((item) => item.trim()).filter((item) => item !== '')
})

const onSubmit = async () => {
  const filteredValues = sanitizeValue.value
  if (values.value.length > MAX_AWB_COUNT) {
    values.value = values.value.slice(0, MAX_AWB_COUNT)
  }

  if (!filteredValues.length) return

  router.push({
    path: `/tracking` + (props.courier ? `/${props.courier.code}` : ''),
    query: {
      order_id: filteredValues.join('|')
    }
  })
}

const { $toast, $recaptcha } = useNuxtApp()
const resultSection = useTemplateRef('resultSection')
const getTracking = useDebounceFn(async () => {
  result.value = undefined
  if (!values.value.length) {
    return
  }

  loading.value = true
  const response = await $fetch('/intl-api/tracking', {
    method: 'POST',
    body: {
      order_id: values.value.filter((item) => item.trim() !== ''),
      captcha: await $recaptcha?.getToken('tracking')
    }
  }).catch((error) => {
    $toast.error(
      error?.data?.message || 'Terjadi kesalahan saat mengambil data tracking'
    )
    loading.value = false
    result.value = {}
  })

  loading.value = false
  result.value = response?.tracking_array

  promiseTimeout(500).then(() => {
    window?.scrollTo({
      top: (resultSection.value?.offsetTop ?? 0) - 90,
      behavior: 'smooth'
    })
  })
}, 500)

watchDebounced(
  () => route.query.order_id,
  () => {
    const rawValues = route.query.order_id?.toString()
    // handle backward shopify bug url if content like this [("value":"KAJ0397135478718")],KAJ0397135478718 into KAJ0397135478718
    if (rawValues?.includes('":"')) {
      const matches = rawValues.match(/"([^"]+)"/g)
      if (matches) {
        values.value = matches
          .map((m) => m.replace(/"/g, ''))
          .filter((m) => m !== 'value')
      }
    } else {
      values.value = rawValues?.split('|') ?? []
    }

    getTracking()
  },
  { immediate: true, debounce: 500 }
)

onMounted(() => {
  input.value?.focus()
})
</script>
<template>
  <HeroBase
    :title="props.title"
    :description="props.description"
    hero-image="/images/home-v4/Lacak-Packet-Banner.webp"
  >
    <template v-if="courier" #above-title>
      <div class="flex gap-3 items-center">
        <UiCourierService
          class="h-6 aspect-video"
          :icon="courier?.icon"
          icon-size="40px"
          :class="courier.class"
        />
        <div class="grow font-semibold">
          {{ courier.courier }}
        </div>
      </div>
    </template>

    <template #action>
      <form class="gap-2 lg:gap-4" @submit.prevent="onSubmit">
        <UiInputControl label="Nomor Resi" for="input" required class="mb-5">
          <UiTagsInput
            id="input"
            ref="input"
            v-model="values"
            :disabled="loading"
            :max="MAX_AWB_COUNT"
            class="bg-background"
            add-on-blur
            add-on-tab
            placeholder="Masukkan Nomor Resi / Order ID (Pisahkan dengan Enter atau Koma)"
            :style="{
              viewTransitionName: 'tracking-input'
            }"
          />
          <p class="text-xs italic">
            Maksimal 3 resi yang dapat dilacak secara bersamaan
          </p>
        </UiInputControl>
        <UiButton
          label="Lacak Paket"
          class="inline-flex"
          :loading="loading"
          :disabled="loading || !sanitizeValue.length"
          :style="{
            viewTransitionName: 'tracking-button'
          }"
        />
      </form>
    </template>
  </HeroBase>
  <section
    v-if="result && values.length > 0"
    ref="resultSection"
    class="py-6 lg:py-10"
  >
    <UiContainer class="space-y-4">
      <TrackingItem
        v-for="(item, index) in values"
        :key="index"
        :entry="item"
        :data="result[item]"
      />
    </UiContainer>
  </section>
</template>
